import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getDeviceFingerprint } from '@/utils/deviceFingerprint';
import { useRealtime } from '@/contexts/RealtimeContext';
import { useDeviceType } from '@/hooks/use-mobile';
import type { Session } from '@supabase/supabase-js';
import { safeError } from '@/lib/safeError';

interface SessionGuardState {
  kicked: boolean;
  kickedByDeviceLabel: string | null;
}

export function useSessionGuard(session: Session | null) {
  const [state, setState] = useState<SessionGuardState>({
    kicked: false,
    kickedByDeviceLabel: null,
  });
  const sessionRowIdRef = useRef<string | null>(null);
  const trackedTokenRef = useRef<string | null>(null);
  const kickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const realtime = useRealtime();
  const rawDeviceType = useDeviceType(); // 'mobile' | 'tablet' | 'desktop'

  // Map tablet -> mobile for the dual-device policy
  const deviceTypeHint = rawDeviceType === 'tablet' ? 'mobile' : rawDeviceType;

  // Track session on login
  const trackSession = useCallback(async (currentSession: Session) => {
    try {
      // Ensure the access token is current before calling the edge function.
      // getSession() only reads from cache — it won't refresh an expired token.
      // We check expiry and force a refresh if the token is within 60s of expiring.
      let { data: { session: freshSession } } = await supabase.auth.getSession();
      if (!freshSession) return;

      const expiresAt = freshSession.expires_at;
      if (expiresAt && expiresAt - Math.floor(Date.now() / 1000) < 60) {
        const { data: refreshed } = await supabase.auth.refreshSession();
        freshSession = refreshed.session;
        if (!freshSession) return;
      }

      const freshToken = freshSession.access_token;
      if (trackedTokenRef.current === freshToken) return;

      const fingerprint = getDeviceFingerprint();

      const res = await supabase.functions.invoke('track-session', {
        headers: { Authorization: `Bearer ${freshToken}` },
        body: {
          session_token: freshToken,
          device_hash: fingerprint.hash,
          device_label: fingerprint.label,
          device_type: deviceTypeHint,
          screen_resolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
        },
      });

      if (res.error) {
        // Silently ignore 401s — they're transient during token refresh
        const is401 = res.error?.message?.includes('non-2xx') || res.error?.name === 'FunctionsHttpError';
        if (!is401) {
          console.error('track-session error:', res.error);
        }
        return;
      }

      const data = res.data as {
        session_id: string;
        had_existing_session: boolean;
        previous_device_label: string | null;
      };

      trackedTokenRef.current = freshToken;
      sessionRowIdRef.current = data.session_id;

      if (data.had_existing_session) {
        toast({
          title: 'Previous session ended',
          description: `Your session on ${data.previous_device_label || 'another device'} was ended.`,
          duration: 6000,
        });
      }
    } catch (err) {
      console.error('Failed to track session:', safeError(err));
    }
  }, [deviceTypeHint]);

  // Track on session change (debounced to let token refresh settle)
  useEffect(() => {
    if (session) {
      const timer = setTimeout(() => trackSession(session), 500);
      return () => clearTimeout(timer);
    } else {
      trackedTokenRef.current = null;
      sessionRowIdRef.current = null;
      setState({ kicked: false, kickedByDeviceLabel: null });
    }
  }, [session, trackSession]);

  // Heartbeat: update last_seen_at every 2 minutes so admins can see who's online
  useEffect(() => {
    if (!session) return;

    const heartbeat = async () => {
      const rowId = sessionRowIdRef.current;
      if (!rowId) return;
      await supabase
        .from('user_sessions')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('id', rowId)
        .eq('is_active', true);
    };

    // First heartbeat after 30s (give session time to register)
    const initialTimer = setTimeout(heartbeat, 30_000);
    // Then every 2 minutes
    const interval = setInterval(heartbeat, 120_000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [session]);

  // Subscribe to realtime updates for kick detection via shared manager
  // Includes 500ms grace period to ignore kicks caused by token refreshes
  useEffect(() => {
    if (!session) return;

    const tokenAtSubscribe = session.access_token;

    const unsub = realtime.subscribe(
      'user_sessions',
      'UPDATE',
      `session_token=eq.${tokenAtSubscribe}`,
      (payload) => {
        const newRow = payload.new as any;
        if (newRow.kicked_at && !newRow.is_active) {
          // Start 500ms grace period — if token changes during this window,
          // it's a token refresh and we should ignore this kick event
          if (kickTimerRef.current) clearTimeout(kickTimerRef.current);

          kickTimerRef.current = setTimeout(() => {
            // If the tracked token is still the same one that got kicked,
            // it's a legitimate kick from another device
            if (trackedTokenRef.current === tokenAtSubscribe) {
              setState({
                kicked: true,
                kickedByDeviceLabel: null,
              });
            }
            // Otherwise token has already rotated — ignore this stale kick
          }, 500);
        }
      }
    );

    return () => {
      unsub();
      if (kickTimerRef.current) {
        clearTimeout(kickTimerRef.current);
        kickTimerRef.current = null;
      }
    };
  }, [session, realtime]);

  // "Stay Signed In" — re-track this session which kicks the other one
  const staySignedIn = useCallback(async () => {
    if (!session) return;
    trackedTokenRef.current = null;
    setState({ kicked: false, kickedByDeviceLabel: null });
    await trackSession(session);
    toast({
      title: 'Session restored',
      description: 'The other session has been signed out.',
      duration: 4000,
    });
  }, [session, trackSession]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  return {
    kicked: state.kicked,
    kickedByDeviceLabel: state.kickedByDeviceLabel,
    staySignedIn,
    signOut,
  };
}
